import React, { useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { BlockNoteView } from "@blocknote/mantine";
import { useCreateBlockNote } from "@blocknote/react";
import "@blocknote/mantine/style.css";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_generate_content_with_ai } from "../../../constants";
import { toast } from "react-toastify";

function AiContentGeneratorModal(props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 900,
    maxHeight: "98vh",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflowY: "auto",
    border: "none",
  };

  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedBlocks, setGeneratedBlocks] = useState(null);

  // Create a preview editor only when we have generated content
  const previewEditor = useCreateBlockNote({
    initialContent: generatedBlocks || [
      {
        id: "initial",
        type: "paragraph",
        props: { textAlignment: "left", textColor: "default" },
        content: [],
        children: [],
      },
    ],
  });

  // Update preview editor when generated blocks change
  React.useEffect(() => {
    if (previewEditor && generatedBlocks) {
      // Clear existing blocks
      const allBlockIds = previewEditor.document.map((block) => block.id);
      previewEditor.removeBlocks(allBlockIds);

      // Insert generated blocks
      if (generatedBlocks.length > 0) {
        previewEditor.insertBlocks(
          generatedBlocks,
          previewEditor.document[0]?.id || null,
          "before"
        );
      }
    }
  }, [generatedBlocks, previewEditor]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt", { autoClose: 3000 });
      return;
    }

    setIsGenerating(true);
    setGeneratedBlocks(null);

    const requestData = {
      company_id: props.homeCompany,
      user_prompt: prompt,
      include_alerts: false,
    };

    try {
      toast.loading("Generating content with AI...", { autoClose: false });

      const res = await postRightworkAPI(
        API_generate_content_with_ai,
        requestData
      );

      if (res.status === 200) {
        const blocks = res.data.redux?.payload?.blocks || [];
        toast.dismiss();
        toast.success(`Generated ${blocks.length} blocks successfully!`, {
          autoClose: 3000,
        });

        // Insert immediately and close
        props.onInsert(blocks);
        handleClose();
      }
    } catch (error) {
      toast.dismiss();
      const errorMessage =
        error.response?.data?.system?.message || "Failed to generate content";
      toast.error(errorMessage, { autoClose: 3000 });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setPrompt("");
    setGeneratedBlocks(null);
    props.handleClose();
  };

  return (
    <Modal
      open={props.open}
      onClose={handleClose}
      aria-labelledby="ai-content-modal-title"
      aria-describedby="ai-content-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 mx-0 mt-0 mb-6 z-[9999]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <AutoAwesomeIcon
                  className="text-violet-600"
                  style={{ fontSize: "20px" }}
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Generate Content with AI
                </h3>
                <p className="text-sm text-slate-600">
                  Describe the content you want and AI will create it for you
                </p>
              </div>
            </div>
            <button
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
              onClick={handleClose}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="px-6 space-y-6">
          {/* Prompt Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Describe the content you want to generate *
            </label>
            <TextField
              placeholder="Example: Create a short, professional guide about proper handwashing procedures in a restaurant kitchen"
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isGenerating}
              InputProps={{
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Tip: Include desired length (short/long) and tone (professional/casual/friendly) in your description
            </p>
          </div>

          {/* Info text */}
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <p className="text-sm text-violet-900">
              Generated content will be inserted into your section editor where you can review and edit it before saving.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 mx-0 mb-0 mt-8 z-10">
          <div className="flex items-center justify-end gap-3">
            <button
              className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
              onClick={handleClose}
              disabled={isGenerating}
            >
              Cancel
            </button>
            <button
              className="px-8 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating...
                </>
              ) : (
                <>
                  <AutoAwesomeIcon style={{ fontSize: "18px" }} />
                  Generate & Insert
                </>
              )}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default AiContentGeneratorModal;
